import useIndexedDB from "@/lib/useIndexedDB";
import { classNames, isTeacher } from "@/lib/util";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react";
import {
  ArrowLongRightIcon,
  Cog6ToothIcon,
  FolderPlusIcon,
  LockClosedIcon,
  LockOpenIcon,
  PencilIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { CurrentUser, SoundContext } from "@/components/Page";
import Link from "next/link";
import {
  OrderCarouselQuery,
  useUpdateManyRecipesMutation,
} from "@/queries/autogenerate/graphql";
import { Dispatch, Fragment, SetStateAction, useContext } from "react";
import toast from "react-hot-toast";
import Select from "react-select";
import useSound from "use-sound";
import TextToggle from "../TextToggle";
import Tooltip from "../styles/Tooltip";
import UndrawEmptyStreet from "../svg/UndrawEmptyStreet";

interface Props {
  user: CurrentUser;
  recipes: OrderCarouselQuery["recipes"];
  sortedFolders: NonNullable<CurrentUser["school"]>["recipeCollections"];
  view: string;
  setView: Dispatch<SetStateAction<string>>;
  orderBy: {
    label: string;
    value: string;
  };
  setOrderBy: (newValue: { label: string; value: string }) => void;
  selectedRecipes: string[];
  openCreateRecipeFolder: () => void;
  openEditRecipeFolder: (id: string) => void;
  openDeleteRecipeFolder: (id: string) => void;
}

const Settings: React.FC<Props> = ({
  user,
  recipes,
  sortedFolders,
  view,
  setView,
  orderBy,
  setOrderBy,
  selectedRecipes,
  openCreateRecipeFolder,
  openEditRecipeFolder,
  openDeleteRecipeFolder,
}) => {
  const [hideSettingsDemo, setHideSettingsDemo] = useIndexedDB(
    "tip3RecipeSettings",
    false
  );

  const soundContext = useContext(SoundContext);
  const [play] = useSound("/static/sounds/pop-up.mp3", {
    volume: 0.25,
    soundEnabled: soundContext.enabled,
  });

  return (
    <Popover className="relative">
      {({ open, close }) => (
        <>
          <PopoverButton
            className={classNames(
              "group fixed right-1 top-14 z-30 flex flex-col items-center justify-center gap-2 rounded-2xl border bg-white px-2 py-8 pr-8 shadow-xl transition",
              open || selectedRecipes.length
                ? "translate-x-full"
                : "translate-x-8 hover:translate-x-6"
            )}
            onClick={() => {
              if (!hideSettingsDemo) {
                setHideSettingsDemo(true);
                play();
              }
            }}
          >
            {hideSettingsDemo ? null : (
              <span className="absolute -left-0.5 -top-0.5 flex h-3 w-3">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex h-3 w-3 rounded-full bg-green-500"></span>
              </span>
            )}
            <Cog6ToothIcon className="w-6" />
            <span
              className="text-base font-semibold text-gray-500 group-hover:text-gray-700"
              style={{
                writingMode: "vertical-lr",
                textOrientation: "mixed",
              }}
            >
              Recipes Settings
            </span>
          </PopoverButton>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1 translate-x-full"
            enterTo="opacity-100 translate-y-0 translate-x-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0 translate-x-0"
            leaveTo="opacity-0 translate-y-1 translate-x-full"
          >
            <PopoverPanel className="fixed right-0 top-14 z-30 w-screen max-w-md transform px-4 sm:px-0">
              <div className="max-h-[calc(95vh-3rem)] overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-black/5">
                {/* <DialogTitle
                  as="h3"
                  className="text-lg font-medium leading-6 text-gray-900"
                >
                  Payment successful
                </DialogTitle>
                <div className="mt-2">
                  <p className="text-sm text-gray-500">
                    Your payment has been successfully submitted. We’ve sent you
                    an email with all of the details of your order.
                  </p>
                </div>

                <div className="mt-4">
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-md border border-transparent bg-blue-100 px-4 py-2 text-sm font-medium text-blue-900 hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                    onClick={closeModal}
                  >
                    Got it, thanks!
                  </button>
                </div> */}
                <header className="sticky top-0 flex w-full items-center justify-between p-2">
                  <h2 className="font-normal">Recipes Page Settings</h2>
                  <button
                    type="button"
                    className="text-sm font-medium text-gray-600 hover:text-gray-900"
                    onClick={close}
                  >
                    Close
                  </button>
                </header>

                <main className="p-2.5">
                  <p className="pb-5">
                    {/* With the following settings, you can customize the recipes
          page to your liking. Whether you want to have a single page or
          an extended one, and much more. */}
                    Customise the recipes page to your liking with the following
                    settings.
                  </p>

                  <section className="mb-8">
                    <h3 className="border-b">View</h3>

                    <div className="p-2">
                      <TextToggle
                        id="recipesViewToggle"
                        checked={view === "grid"}
                        checkedText="Grid"
                        uncheckedText="List"
                        onChange={(e) =>
                          setView(e?.target.checked ? "grid" : "list")
                        }
                        width="100%"
                      />
                    </div>
                  </section>

                  <section className="mb-8">
                    <h3 className="border-b">Sort Recipes By...</h3>

                    <Select
                      value={orderBy}
                      options={[
                        { label: "Alphabetical", value: "name_ASC" },
                        {
                          label: "Alphabetical (Descending)",
                          value: "name_DESC",
                        },
                        { label: "Oldest", value: "createdAt_ASC" },
                        { label: "Newest", value: "createdAt_DESC" },
                        { label: "First Updated", value: "updatedAt_ASC" },
                        { label: "Last Updated", value: "updatedAt_DESC" },
                        { label: "Tags", value: "tag_ASC" },
                        { label: "Tags (Descending)", value: "tag_DESC" },
                        { label: "Class", value: "class_ASC" },
                        { label: "Class (Descending)", value: "class_DESC" },
                      ]}
                      noOptionsMessage={() => "No Options"}
                      onChange={setOrderBy}
                      isSearchable={false}
                      isClearable={false}
                      className="w-full"
                      styles={{
                        container: (provided) => ({
                          ...provided,
                          display: "inline-block",
                          //   width: "150px",
                          margin: "5px",
                          zIndex: 30,
                        }),
                      }}
                    />
                  </section>

                  {isTeacher(user) ? (
                    <section className="mb-16">
                      <h3 className="border-b">Lock/Unlock Recipes</h3>

                      <div className="">
                        {!recipes.every((r) => r.locked) ? (
                          <LockAllRecipesButton
                            recipeIDs={
                              selectedRecipes.length
                                ? selectedRecipes
                                : recipes.map((r) => r.id)
                            }
                            selected={!!selectedRecipes.length}
                          />
                        ) : null}
                        {!recipes.every((r) => !r.locked) ? (
                          <UnlockAllRecipesButton
                            recipeIDs={
                              selectedRecipes.length
                                ? selectedRecipes
                                : recipes.map((r) => r.id)
                            }
                            selected={!!selectedRecipes.length}
                          />
                        ) : null}
                      </div>
                    </section>
                  ) : null}

                  <section className="mb-16">
                    <div className="flex items-end justify-between border-b px-2">
                      <h3 className="">Folders</h3>
                      <p className="font-normal text-gray-500">
                        {sortedFolders.length} Folders
                      </p>
                    </div>

                    {sortedFolders.length ? (
                      <ul className="p-2">
                        {sortedFolders
                          .filter((rc) => !rc.belongsToId)
                          .map((folder) => (
                            <li key={folder.id} className="space-y-1">
                              <div className="flex justify-between">
                                <Link
                                  prefetch={false}
                                  href={`recipes/?folder=${folder.id}`}
                                  className="font-normal"
                                >
                                  {folder.name}
                                </Link>
                                <div className="flex items-center gap-1">
                                  <span className="text-gray-500">
                                    {
                                      recipes.filter(
                                        (r) =>
                                          r.recipeCollection?.id === folder.id
                                      ).length
                                    }{" "}
                                    Recipes
                                  </span>
                                  <Tooltip content="Delete Folder">
                                    <button
                                      onClick={() =>
                                        openDeleteRecipeFolder(folder.id)
                                      }
                                    >
                                      <TrashIcon className="w-4 transform text-orange-600 hover:scale-110 hover:text-orange-700" />
                                    </button>
                                  </Tooltip>
                                  <Tooltip content="Edit Folder">
                                    <button
                                      onClick={() =>
                                        openEditRecipeFolder(folder.id)
                                      }
                                    >
                                      <PencilIcon className="w-4 transform hover:scale-110 hover:text-gray-900" />
                                    </button>
                                  </Tooltip>
                                </div>
                              </div>
                              {sortedFolders
                                .filter((f) => f.belongsToId === folder.id)
                                .map((child) => (
                                  <RecursiveFolderDisplay
                                    key={child.id}
                                    folder={child}
                                    folders={sortedFolders}
                                    recipes={recipes}
                                    openEditRecipeFolder={openEditRecipeFolder}
                                    openDeleteRecipeFolder={
                                      openDeleteRecipeFolder
                                    }
                                  />
                                ))}
                            </li>
                          ))}
                      </ul>
                    ) : (
                      <div className="flex flex-col items-center justify-center pt-10">
                        <UndrawEmptyStreet className="w-88" />
                        <div className="mt-4 text-center">
                          <h1 className="">No Folders</h1>
                          {/* <p className="mb-8">
                            Term dates are required to set up your schedule.
                          </p> */}
                          <button
                            className="btn btn-primary"
                            onClick={openCreateRecipeFolder}
                          >
                            <FolderPlusIcon className="w-5" /> Create Folder
                          </button>
                        </div>
                      </div>
                    )}

                    {/* <div className="flex items-center gap-2">
                      <button className="btn">Create Folder</button>
                      <button className="btn">Delete Folder</button>
                    </div> */}
                  </section>

                  {/* Dange Zone */}
                </main>
                {/* <div className="relative grid gap-8 bg-white p-7 lg:grid-cols-2"></div>
                <div className="bg-gray-50 p-4">
                  <a
                    href="##"
                    className="flow-root rounded-md px-2 py-2 transition duration-150 ease-in-out hover:bg-gray-100 focus:outline-none focus-visible:ring focus-visible:ring-orange-500/50"
                  >
                    <span className="flex items-center">
                      <span className="text-sm font-medium text-gray-900">
                        Documentation
                      </span>
                    </span>
                    <span className="block text-sm text-gray-500">
                      Start integrating products and tools
                    </span>
                  </a>
                </div> */}
              </div>
            </PopoverPanel>
          </Transition>
        </>
      )}
    </Popover>
  );
};

interface LockAllRecipesButtonProps {
  recipeIDs: string[];
  selected?: boolean;
}

export const LockAllRecipesButton: React.FC<LockAllRecipesButtonProps> = ({
  recipeIDs,
  selected,
}) => {
  const [{ fetching }, updateManyRecipes] = useUpdateManyRecipesMutation();

  return (
    <button
      className="btn flex items-center gap-1"
      onClick={async () => {
        toast
          .promise(
            updateManyRecipes({
              where: { id: { in: recipeIDs } },
              data: {
                locked: { set: true },
              },
            }),
            {
              loading: "Locking Recipes...",
              success: "Recipes Locked!",
              error: "Error Locking Recipes",
            }
          )
          .catch((error) => console.error(error));
      }}
    >
      {fetching ? (
        <div className="spinner size-5" />
      ) : (
        <LockClosedIcon className="w-5" />
      )}
      <span>Lock {selected ? "Selected" : "All"} Recipes</span>
    </button>
  );
};

interface UnlockAllRecipesButtonProps {
  recipeIDs: string[];
  selected?: boolean;
}

export const UnlockAllRecipesButton: React.FC<UnlockAllRecipesButtonProps> = ({
  recipeIDs,
  selected,
}) => {
  const [{ fetching }, updateManyRecipes] = useUpdateManyRecipesMutation();

  return (
    <button
      className="btn flex items-center gap-1"
      onClick={async () => {
        toast
          .promise(
            updateManyRecipes({
              where: { id: { in: recipeIDs } },
              data: {
                locked: { set: false },
              },
            }),
            {
              loading: "Unlocking Recipes...",
              success: "Recipes Unlocked!",
              error: "Error Unlocking Recipes",
            }
          )
          .catch((error) => console.error(error));
      }}
    >
      {fetching ? (
        <div className="spinner size-5" />
      ) : (
        <LockOpenIcon className="w-5" />
      )}
      <span>Unlock {selected ? "The Selected" : "All"} Recipes</span>
    </button>
  );
};

interface RecursiveFolderDisplayProps {
  folder: NonNullable<CurrentUser["school"]>["recipeCollections"][0];
  folders: NonNullable<CurrentUser["school"]>["recipeCollections"];
  recipes: OrderCarouselQuery["recipes"];
  openEditRecipeFolder: (id: string) => void;
  openDeleteRecipeFolder: (id: string) => void;
}

const RecursiveFolderDisplay: React.FC<RecursiveFolderDisplayProps> = ({
  folder,
  folders,
  recipes,
  openEditRecipeFolder,
  openDeleteRecipeFolder,
}) => {
  const children = folders.filter((f) => f.belongsToId === folder.id);

  return (
    <ul className="space-y-1 pl-4">
      <li>
        <div className="flex justify-between">
          <span>
            <ArrowLongRightIcon className="w-5 text-gray-400" />{" "}
            <Link
              prefetch={false}
              href={`recipes/?folder=${folder.id}`}
              className="font-normal"
            >
              {folder.name}
            </Link>
          </span>
          <div className="flex items-center gap-1">
            <span className="text-gray-500">
              {
                recipes.filter((r) => r.recipeCollection?.id === folder.id)
                  .length
              }{" "}
              Recipes
            </span>
            <Tooltip content="Delete Folder">
              <button onClick={() => openDeleteRecipeFolder(folder.id)}>
                <TrashIcon className="w-4 transform text-orange-600 hover:scale-110 hover:text-orange-700" />
              </button>
            </Tooltip>
            <Tooltip content="Edit Folder">
              <button onClick={() => openEditRecipeFolder(folder.id)}>
                <PencilIcon className="w-4 transform hover:scale-110 hover:text-gray-900" />
              </button>
            </Tooltip>
          </div>
        </div>
      </li>
      {children.length
        ? children.map((child) => (
            <RecursiveFolderDisplay
              key={child.id}
              folder={child}
              folders={folders}
              recipes={recipes}
              openEditRecipeFolder={openEditRecipeFolder}
              openDeleteRecipeFolder={openDeleteRecipeFolder}
            />
          ))
        : null}
    </ul>
  );
};

export default Settings;
